import { useContext, useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import * as routes from "../../../routes";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { RoleValidator, SearchableDropdown, Spinner } from "../../../Components/Common";
import { useAdvanceNote } from "../Hooks/useAdvanceNote";
import {
  ADVANCE_NOTE_MODES,
  ADVANCE_NOTE_STATUS,
  ADVANCE_NOTE_STATUS_LABELS,
  getAdvanceNoteStatusColor,
} from "./constants";
import { formatDate } from "../../../Utils/timeUtils";
import { ROLES } from "../../../Constants/Strings";
import { UserContext } from "../../../Context";

const validationSchema = Yup.object().shape({
  user: Yup.string().required("User is required"),
  category: Yup.string().required("Category is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .positive("Amount must be positive")
    .required("Amount is required"),
  date: Yup.date().required("Date is required"),
  site: Yup.string().required("Site is required"),
});

const AdvanceNoteAction = () => {
  const navigate = useNavigate();
  const param = useParams();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const {
    createAdvanceNoteData,
    editAdvanceNoteData,
    fetchAdvanceNoteById,
    fetchUsers,
    fetchSites,
    fetchCategoriesList,
  } = useAdvanceNote();

  const [mode, setMode] = useState(ADVANCE_NOTE_MODES.CREATE);
  const [pageLoading, setPageLoading] = useState(true);

  const todaysDate = new Date().toISOString().split("T")[0];

  const [initialValues, setInitialValues] = useState({
    user: "",
    site: "",
    category: "",
    project: "",
    amount: "",
    note: "",
    status: ADVANCE_NOTE_STATUS.DRAFT,
    date: todaysDate, // auto-fill today's date
    user_name: "",
    category_name:"",
    site_name:""
  });

  const getAdvanceNoteData = async () => {
    const data = await fetchAdvanceNoteById(param.instanceId);
    if (data) {
      setInitialValues({
        user: data.user || "",
        site: data.site || "",
        category: data.category || "",
        project: data.project_name || "",
        amount: data.amount || "",
        note: data.note || "",
        status: data.status || ADVANCE_NOTE_STATUS.DRAFT,
        date: data.date || todaysDate,
        user_name: data.user_name,
        site_name: data.site_name,
        category_name: data.category_name
      });
    }
    setPageLoading(false);
  };

  useEffect(() => {
    if (param.instanceId) {
      getAdvanceNoteData();
      if (location.pathname.includes(routes.EDIT)) {
        setMode(ADVANCE_NOTE_MODES.EDIT);
      } else if (location.pathname.includes(routes.VIEW)) {
        setMode(ADVANCE_NOTE_MODES.VIEW);
      }
    } else {
      setMode(ADVANCE_NOTE_MODES.CREATE);
      setPageLoading(false);
    }
  }, [param.instanceId, location.pathname]);

  const handleSubmit = async (values, { setSubmitting }, statusType) => {
    const payload = {
      ...values,
      status: statusType,
      date: values.date,
    };

    let data = null;

    if (mode === ADVANCE_NOTE_MODES.CREATE) {
      data = await createAdvanceNoteData(payload);
    } else{
      data = await editAdvanceNoteData(param.instanceId, payload);
    }

    if (data?.id) {
      navigate(routes.ADVANCE_NOTES_LIST);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Spinner />
      </div>
    );
  }

  const isView = mode === ADVANCE_NOTE_MODES.VIEW;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center bg-white pb-1 mb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-primary hover:text-blue-700 transition"
          >
            <ArrowLeft size={25} />
          </button>
          <h2 className="text-lg md:text-2xl font-semibold text-gray-800">
            {mode} Advance Note
          </h2>
        </div>
      </div>

      {/* Form */}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isView ? null : validationSchema}
        onSubmit={(values, actions) =>
          handleSubmit(
            values,
            actions,
            ADVANCE_NOTE_STATUS.PENDING_FOR_APPROVAL
          )
        }
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form className="px-6 py-0.5 max-w-4xl mx-auto">
            <div className="flex flex-col gap-3">
              {/* User Dropdown */}
              <SearchableDropdown
                label={
                  <span>
                    User <sub className="text-red-500 top-0">*</sub>
                  </span>
                }
                fetchFunction={fetchUsers}
                onSelect={(val) => setFieldValue("user", val)}
                valueKey="id"
                labelKey="first_name"
                placeholder={
                  mode === ADVANCE_NOTE_MODES.CREATE
                    ? "Select User"
                    : initialValues.user_name
                }
                disabled={isView}
              />
              <ErrorMessage
                name="user"
                component="div"
                className="text-red-500 text-sm m-0"
              />

              {/* Site & Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <SearchableDropdown
                    label={
                      <span>
                        Site <sub className="text-red-500">*</sub>
                      </span>
                    }
                    fetchFunction={fetchSites}
                    onSelect={(val) => setFieldValue("site", val)}
                    valueKey="id"
                    labelKey="site_name"
                    placeholder={
                      mode === ADVANCE_NOTE_MODES.CREATE
                        ? "Select Site"
                        : initialValues.site_name
                    }
                    disabled={isView}
                  />
                  <ErrorMessage
                    name="site"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                <div>
                  <SearchableDropdown
                    label={
                      <span>
                        Category <sub className="text-red-500">*</sub>
                      </span>
                    }
                    fetchFunction={fetchCategoriesList}
                    onSelect={(val) => setFieldValue("category", val)}
                    valueKey="id"
                    labelKey="name"
                    placeholder={
                      mode === ADVANCE_NOTE_MODES.CREATE
                        ? "Select Site"
                        : initialValues.category_name
                    }
                    disabled={isView}
                  />
                  <ErrorMessage
                    name="category"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>
              </div>

              {/* Amount, Date, and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Amount */}
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount <sub className="text-red-500 top-0">*</sub>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-700">
                      ₹
                    </span>
                    <Field
                      type="number"
                      name="amount"
                      disabled={isView}
                      placeholder="Enter amount"
                      className="pl-7 w-full rounded-lg px-3 py-2 font-normal text-gray-700 border-gray-300"
                    />
                  </div>
                  <ErrorMessage
                    name="amount"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                </div>

                {/* Date & Status (inline) */}
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    {isView ? (
                      <span>{formatDate(values.date)}</span>
                    ) : (
                      <Field
                        type="date"
                        name="date"
                        disabled={isView}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:ring-primary"
                      />
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex flex-row justify-center items-center gap-3 pt-5 pr-2">
                    <label className="text-sm font-medium text-gray-700 mb-1">
                      Status:
                    </label>
                    <span
                      className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold ${getAdvanceNoteStatusColor(
                        values.status
                      )}`}
                    >
                      {ADVANCE_NOTE_STATUS_LABELS[values.status]}
                    </span>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <Field
                  as="textarea"
                  name="note"
                  disabled={isView}
                  placeholder="Enter note"
                  rows={3}
                  className="w-full border-gray-300 rounded-lg px-3 py-2 text-gray-700 resize-none"
                />
              </div>
            </div>

            {/* Action Buttons */}
            {!isView &&
              values.status === ADVANCE_NOTE_STATUS.DRAFT &&
              user.role === ROLES.PROJECT_MANAGER && (
                <div className="flex justify-end mt-2 pt-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleSubmit(
                        values,
                        { setSubmitting: () => {} },
                        ADVANCE_NOTE_STATUS.DRAFT
                      )
                    }
                    className="px-5 py-2.5 rounded-lg font-medium transition-all duration-200 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Save as Draft
                  </button>

                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-lg shadow-md font-medium transition-all duration-200 bg-primary text-white hover:bg-blue-700"
                  >
                    Submit for Approval
                  </button>
                </div>
              )}

            {/* Billing Incharge Approve button */}
            {isView &&
              values.status === ADVANCE_NOTE_STATUS.PENDING_FOR_APPROVAL &&
              user.role === ROLES.BILLING_INCHARGE && (
                <div className="flex justify-end mt-2 pt-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleSubmit(
                        values,
                        { setSubmitting: () => {} },
                        ADVANCE_NOTE_STATUS.APPROVED
                      )
                    }
                    className="px-5 py-2.5 rounded-lg shadow-md font-medium transition-all duration-200 bg-green-600 text-white hover:bg-green-700"
                  >
                    Approve
                  </button>
                </div>
              )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AdvanceNoteAction;
